/**
 * NDA-gated access — client side (#529, #795 R4).
 *
 * The backend NDA gate blocks pending industry-player users, redirecting HTML
 * loads to `/nda-pending` and answering `/api/*` with a 403. That covers full
 * page loads and API-driven views, but a pending user navigating client-side to
 * a view that fires no gate-checked API call would otherwise sit there with a
 * raw `nda_pending` error banner. To close that gap the server injects a
 * `bf-nda-pending` meta tag (the same predicate the gate uses); the SPA checks
 * it on every route change and redirects proactively.
 *
 * Unlike the ToS gate there is no in-session "satisfied" flag: NDA verification
 * is performed externally by an admin, so the pending state only clears on a
 * fresh login (and thus a fresh meta tag).
 */

import { navigation } from '@brainfocus/web/api/client';

/** Whether the current user is a pending industry-player awaiting NDA verification. */
export function needsNdaVerification(): boolean {
    const meta = document.head.querySelector('meta[name=bf-nda-pending]') as HTMLMetaElement | null;
    return meta?.content === 'true';
}

/**
 * Redirect the browser (full-page) to `/nda-pending` when the current user is
 * pending NDA verification and we are not already on that page. Returns true if
 * a redirect was issued. Full-page navigation tears down the current view and
 * lets the backend gate re-evaluate the load — consistent with the `execFetch`
 * 403 handling.
 */
export function redirectToNdaPendingIfNeeded(): boolean {
    if (!needsNdaVerification()) {
        return false;
    }
    if (window.location.pathname === '/nda-pending') {
        return false;
    }
    navigation.assign('/nda-pending');
    return true;
}
